import { useCanWriteWorkspaceAsset } from '@vizit/access';
import type { ImageView } from '@vizit/api';
import { useAssetFavorites, useBulkSetAssetFavoriteMutation } from '@vizit/api';
import { AlertLevel, useToast } from '@vizit/atoms';
import { CheckSquareFilledIcon, HeartFilledIcon, HeartIcon } from '@vizit/icons';
import { type BulkAction, BulkActionBar } from '@vizit/molecules';
import * as React from 'react';

interface CollectionBulkActionBarProps {
  /**
   * Selected assets, in grid order.
   *
   * @required
   */
  selectedAssets: ImageView[];
  /**
   * How many assets are shown now, for "Select visible".
   *
   * @required
   */
  visibleCount: number;
  /**
   * Route workspace, used for assets whose payload has no `workspace_id`.
   *
   * @required
   */
  workspaceId: string;
  /**
   * Selects every asset shown now.
   *
   * @required
   */
  onSelectVisible: () => void;
  /**
   * Clears the selection (× button and Esc).
   *
   * @required
   */
  onClear: () => void;
  /**
   * Whether favorites are on for this user. Without it there is no Favorite action.
   *
   * @optional
   * @default true
   */
  showFavorite?: boolean;
}

/**
 * Action bar for the Collection grid selection (BLU-348): "Select visible" and Favorite. Compare stays hidden until
 * Comparison is released (SDD D42); Delete and Download come with their own tickets.
 */
function CollectionBulkActionBar(props: CollectionBulkActionBarProps) {
  const { selectedAssets, visibleCount, workspaceId, onSelectVisible, onClear, showFavorite = true } = props;
  const { isFavorite } = useAssetFavorites();
  const canFavorite = useCanWriteWorkspaceAsset();
  const { addToast } = useToast();
  const { mutate: setFavorites } = useBulkSetAssetFavoriteMutation({
    onError: () => {
      addToast({
        title: 'Could not update favorites',
        description: 'Nothing was changed. Please try again.',
        level: AlertLevel.Error,
      });
    },
  });

  // A write is addressed at each asset's own workspace (older payloads may omit it, then the route's)
  const workspaceOf = React.useCallback((asset: ImageView) => asset.workspace_id ?? workspaceId, [workspaceId]);

  // When everything selected is already a favorite, the action removes them instead (SDD D41)
  const isAllFavorite = selectedAssets.length > 0 && selectedAssets.every((asset) => isFavorite(asset));
  const canFavoriteAll = selectedAssets.every((asset) => canFavorite({ workspace_id: workspaceOf(asset) }));
  // The bulk endpoint is all or nothing within one workspace. Splitting a mixed selection into one request per
  // workspace could leave it half changed, so such a selection can't be favorited at once
  const selectedWorkspaceIds = new Set(selectedAssets.map(workspaceOf));
  const [selectedWorkspaceId] = selectedWorkspaceIds;
  // An empty id (no `workspace_id` on the asset and none from the route) counts as no workspace, so the button is
  // disabled instead of doing nothing on press
  const isSingleWorkspace = selectedWorkspaceIds.size === 1 && Boolean(selectedWorkspaceId);

  const handleFavorite = React.useCallback(() => {
    if (!isSingleWorkspace || !selectedWorkspaceId) return;
    setFavorites({
      workspace_id: selectedWorkspaceId,
      image_ids: selectedAssets.map((asset) => asset.id),
      is_favorite: !isAllFavorite,
    });
  }, [isSingleWorkspace, selectedWorkspaceId, selectedAssets, setFavorites, isAllFavorite]);

  const isAllVisibleSelected = selectedAssets.length >= visibleCount;

  const actions: BulkAction[] = [
    {
      key: 'select-visible',
      label: 'Select visible',
      icon: <CheckSquareFilledIcon size={15} />,
      onPress: onSelectVisible,
      isDisabled: isAllVisibleSelected,
      // Only the images loaded so far: scrolling loads more and the action is available again
      title: `Select all ${visibleCount} images loaded so far`,
      disabledReason: 'All loaded images are selected. Scroll to load more',
    },
    ...(showFavorite
      ? [
          {
            key: 'favorite',
            label: isAllFavorite ? 'Unfavorite' : 'Favorite',
            icon: isAllFavorite ? <HeartFilledIcon size={15} /> : <HeartIcon size={15} />,
            onPress: handleFavorite,
            isDisabled: !canFavoriteAll || !isSingleWorkspace,
            disabledReason: isSingleWorkspace
              ? "You don't have access to change favorites for some of these images"
              : 'Select images from one workspace to change favorites',
          },
        ]
      : []),
  ];

  return <BulkActionBar count={selectedAssets.length} actions={actions} onClear={onClear} />;
}

export { CollectionBulkActionBar };
export type { CollectionBulkActionBarProps };
